import React, { useCallback, useEffect, useRef, useState } from "react";
import apiService from "../../services/api";

type Props = {
  handleChangeChatData: (message: string, channel?: string) => void | Promise<void>;
  leadId?: number | string;
  answerType?: string;
};

const SuggestedInput: React.FC<Props> = ({ handleChangeChatData, leadId, answerType }) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedChannel, setSelectedChannel] = useState<string>("SMS"); // Default to SMS for Suggested leads
  const [loading, setLoading] = useState<boolean>(false);
  // Default to true so send button works when consent UI is hidden (checkbox is commented out)
  const [smsConsent, setSmsConsent] = useState<boolean>(true);
  const consentKey = leadId == null ? undefined : `smsConsent:${String(leadId)}`;
  
  // Debug logging
  console.log('🔍 [DEBUG] SuggestedInput render:', {
    selectedChannel,
    leadId,
    loading
  });

  const loadingRef = useRef(false);
  const lastFetchedForRef = useRef<string | undefined>(undefined);
  const lastSuggestionRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const lastEventIdRef = useRef<string | undefined>(undefined);

  const leadKey = leadId == null ? undefined : String(leadId);

  const fetchSuggestion = useCallback(
    async (reason: "lead-change" | "client-message", eventId?: string) => {
      if (!leadKey) return;
      if (loadingRef.current) return;

      const dedupeKey = `${reason}:${leadKey}:${eventId ?? ""}`;
      if (lastFetchedForRef.current === dedupeKey) return;
      lastFetchedForRef.current = dedupeKey;

      loadingRef.current = true;
      setLoading(true);
      try {
        let { suggestion } = await apiService.getSuggestedResponse(leadKey);

        // If same as before try lightweight retry
        const prev = (lastSuggestionRef.current || "").trim();
        if ((suggestion || "").trim() && (suggestion || "").trim() === prev) {
          const second = await apiService.getSuggestedResponse(leadKey);
          if ((second.suggestion || "").trim()) suggestion = second.suggestion;
        }

        setInputValue(suggestion || "");
        lastSuggestionRef.current = suggestion || "";
      } catch (e) {
        console.error("[suggest] fetch error:", e);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [leadKey]
  );

  // Listen for conversations fetching event to start loading immediately
  useEffect(() => {
    const handlerFetching = (evt: Event) => {
      const custom = evt as CustomEvent<{ leadId?: string | number; timestamp?: number }>;
      const evtLeadId = custom?.detail?.leadId == null ? undefined : String(custom.detail.leadId);
      
      // Only show loading if this event is for the current lead and answerType is "Suggested"
      if (!leadKey || !evtLeadId || evtLeadId !== leadKey) return;
      if (answerType !== "Suggested") return;
      
      // Start loading immediately when conversations API is called (for Suggested Response mode)
      setLoading(true);
    };

    window.addEventListener("myhomey:conversations-fetching", handlerFetching as EventListener);
    return () => {
      window.removeEventListener("myhomey:conversations-fetching", handlerFetching as EventListener);
    };
  }, [leadKey, answerType]);

  // Listen for conversations fetched event - refresh suggested response when conversations are fetched
  useEffect(() => {
    const handler = (evt: Event) => {
      const custom = evt as CustomEvent<{ leadId?: string | number; timestamp?: number }>;
      const evtLeadId = custom?.detail?.leadId == null ? undefined : String(custom.detail.leadId);
      
      // Only update if this event is for the current lead and answerType is "Suggested"
      if (!leadKey || !evtLeadId || evtLeadId !== leadKey) return;
      if (answerType !== "Suggested") return;

      // Refresh suggested response when conversations are fetched
      const refreshSuggestion = async () => {
        try {
          await fetchSuggestion("client-message", `refresh-${Date.now()}`);
        } finally {
          setLoading(false);
        }
      };

      refreshSuggestion();
    };

    window.addEventListener("myhomey:conversations-fetched", handler as EventListener);
    return () => {
      window.removeEventListener("myhomey:conversations-fetched", handler as EventListener);
    };
  }, [leadKey, answerType, fetchSuggestion]);

  // Initialize consent from localStorage per lead
  useEffect(() => {
    try {
      if (consentKey) {
        const saved = localStorage.getItem(consentKey);
        if (saved === 'true') setSmsConsent(true);
      }
    } catch {}
  }, [consentKey]);

  // Persist consent when it becomes true
  useEffect(() => {
    try {
      if (consentKey) {
        if (smsConsent) localStorage.setItem(consentKey, 'true');
      }
    } catch {}
  }, [smsConsent, consentKey]);

  const scheduleFetch = useCallback(
    (reason: "lead-change" | "client-message", eventId?: string) => {
      if (eventId) lastEventIdRef.current = eventId;

      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        fetchSuggestion(reason, lastEventIdRef.current);
      }, 250) as unknown as number;
    },
    [fetchSuggestion]
  );

  // 1) Fetch on lead change
  useEffect(() => {
    setInputValue("");
    if (leadKey) {
      scheduleFetch("lead-change", `lead-${leadKey}-${Date.now()}`);
    }
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, [leadKey, scheduleFetch]);

  // 2) Listen for "client message arrived" custom event (emit from LeadContent)
  useEffect(() => {
    const handler = (evt: Event) => {
      const custom = evt as CustomEvent<{ leadId?: string | number; messageId?: string | number }>;
      const evtLead = custom?.detail?.leadId == null ? undefined : String(custom.detail.leadId);
      if (!leadKey || !evtLead || evtLead !== leadKey) return;

      const evtId =
        custom?.detail?.messageId != null ? String(custom.detail.messageId) : `evt-${Date.now()}`;

      scheduleFetch("client-message", evtId);
    };

    window.addEventListener("myhomey:new-client-message", handler as EventListener);
    return () => {
      window.removeEventListener("myhomey:new-client-message", handler as EventListener);
    };
  }, [leadKey, scheduleFetch]);

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (e.key === "Enter" && inputValue) {
      if (selectedChannel === "SMS" && !smsConsent) {
        e.preventDefault();
        return;
      }
      await handleChangeChatData(inputValue, selectedChannel);
      setInputValue("");
      // Refetch comes from LeadContent event
    }
  };

  return (
    <div className="flex flex-col gap-y-2 w-[74%] mt-3 mb-6 border border-lightGray rounded-2xl px-6 py-4">
      {/* Channel Selector + inline SMS consent checkbox (left) */}
      <div className="flex items-center justify-between">
        {/* {selectedChannel === "SMS" && !localStorage.getItem(consentKey || '') ? (
          <div className="text-[12px] text-darkGray flex items-center gap-2 mr-3">
            <input
              id="sms-consent-suggested"
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              className="mt-[1px]"
            />
            <label htmlFor="sms-consent-suggested" className="leading-snug">
              I confirm recipient consent for SMS. <span className="underline cursor-help" title="By sending, you confirm prior express consent to receive SMS updates. Message/data rates may apply. Reply STOP to opt out.">Details</span>
            </label>
          </div>
        ) : <div />} */}
        <select
          value={selectedChannel}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedChannel(value);
            if (value !== "SMS" && smsConsent) setSmsConsent(false);
          }}
          className="text-sm px-4 py-2 pr-8 border border-lightGray rounded-full bg-white text-darkGray shadow-sm focus:border-darkBlue focus:ring-2 focus:ring-darkBlue/30 hover:border-darkBlue transition-colors outline-none"
        >
          <option value="SMS">📱 Send via SMS</option>
          <option value="EMAIL">📧 Send via Email</option>
        </select>
      </div>
      {/* Temporary: show full consent text visibly for screenshots */}
      {/* {selectedChannel === "SMS" && (
        <div className="mt-1 text-[12px] text-darkGray bg-lightestGray border border-lightGray rounded-md px-3 py-2">
          By sending, you confirm prior express consent to receive SMS updates. Message/data rates may apply. Reply STOP to opt out.
        </div>
      )} */}
      {/* Compact consent banner with tooltip for proof */}
      {/* <div className="flex items-center gap-2 text-[12px] text-mediumGray">
        <span className="px-2 py-1 bg-lightestGray border border-lightGray rounded-full">SMS consent required</span>
        <i
          className="fa-regular fa-circle-question text-darkGray"
          title="By providing a phone number, you agree to receive SMS updates. Message/data rates may apply. Reply STOP to opt out."
        ></i>
      </div> */}
      {/* Checkbox is shown inline with selector above when SMS is selected */}
      
      <div className="flex flex-row justify-between items-center gap-x-4">
        <div className="flex flex-col gap-y-2 w-full">
          <p className="text-h3 font-medium text-darkerGray">
            <span className="text-darkBlue pr-1">|</span>Suggested Response - Click Below
          </p>
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={onKeyDown}
            type="text"
            placeholder={
              loading
                ? "Updating suggestion…"
                : "Type your message."
            }
            className="rounded-sm border border-blue border-dashed px-3 py-2 bg-[#F9FAFA] text-h3 text-darkestSlate outline-none placeholder:text-darkGray "
          />
        </div>
        <div className="flex items-end justify-end h-full py-2">
        <button
          // disabled={!inputValue || loading || (selectedChannel === "SMS" && !smsConsent && !localStorage.getItem(consentKey || ''))}
          onClick={async () => {
            if (!inputValue) return;
            if (selectedChannel === "SMS" && !smsConsent && !localStorage.getItem(consentKey || '')) return;
            await handleChangeChatData(inputValue, selectedChannel);
            setInputValue("");
          }}
        >
          <i className="fa-solid fa-paper-plane text-darkGray"></i>
        </button>
        </div>
      </div>
    </div>
  );
};

export default SuggestedInput;